import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import NotificationContext from '../context/NotificationContext';
import {
    Box, CircularProgress, Container, Button, Typography,
    FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Chip, Switch, FormControlLabel,
    IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import SendIcon from '@mui/icons-material/Send';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ParentsComponent from '../components/ParentsComponent';
import { useTranslation } from 'react-i18next';
import { NAV_BG, AMBER, AMBER_DARK, AMBER_GLOW } from '../theme';

function RichTextEditor({ value, onChange }) {
    const editorRef = useRef(null);
    const execCmd = (cmd) => {
        editorRef.current.focus();
        document.execCommand(cmd, false, null);
        onChange(editorRef.current.innerHTML);
    };
    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value || '';
        }
    // eslint-disable-next-line
    }, []);
    const toolbarBtn = (title, icon, cmd) => (
        <Tooltip title={title}>
            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); execCmd(cmd); }}
                sx={{ color: '#475569', '&:hover': { color: AMBER } }}>
                {icon}
            </IconButton>
        </Tooltip>
    );
    return (
        <Box sx={{ border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.5, borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                {toolbarBtn('Bold', <FormatBoldIcon fontSize="small" />, 'bold')}
                {toolbarBtn('Italic', <FormatItalicIcon fontSize="small" />, 'italic')}
                {toolbarBtn('Underline', <FormatUnderlinedIcon fontSize="small" />, 'underline')}
                {toolbarBtn('Bullet list', <FormatListBulletedIcon fontSize="small" />, 'insertUnorderedList')}
            </Box>
            <Box ref={editorRef} contentEditable suppressContentEditableWarning
                onInput={() => onChange(editorRef.current.innerHTML)}
                sx={{ minHeight: 120, p: 1.5, outline: 'none', fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', lineHeight: 1.6, color: '#1e293b', '& ul': { pl: 2.5 } }}
            />
        </Box>
    );
}


const ParentsPage = () => {
    const { authTokens, logoutUser, backendUrl, pageTitle } = useContext(AuthContext);
    const { showToast } = useContext(NotificationContext);
    const [parents, setParents] = useState([]);
    const [classes, setClasses] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [selectedClassId, setSelectedClassId] = useState('');
    const [selectedDeptId, setSelectedDeptId] = useState('');
    const [selectedParentIds, setSelectedParentIds] = useState([]);

    const [showNoChildren, setShowNoChildren] = useState(false);
    const [showInactive, setShowInactive] = useState(true);
    const [showDeleted, setShowDeleted] = useState(false);

    const [msgOpen, setMsgOpen] = useState(false);
    const [msgTitle, setMsgTitle] = useState('');
    const [msgText, setMsgText] = useState('');
    const [msgSending, setMsgSending] = useState(false);

    const navigate = useNavigate();
    const { t } = useTranslation();

    document.title = pageTitle + ' - ' + t('parents.title');

    const authHeader = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + String(authTokens.access)
    }), [authTokens]);

    const getParents = useCallback(async () => {
        try {
            const response = await fetch(backendUrl + 'api/parents/', { method: 'GET', headers: authHeader() });
            if (response.status === 401) { logoutUser(); return; }
            const data = await response.json();
            setParents(data);
        } catch { showToast(t('common.network_error'), 'error'); }
    }, [authHeader, backendUrl, logoutUser]);

    const getClasses = useCallback(async () => {
        const response = await fetch(backendUrl + 'api/get-classes/', { headers: authHeader() });
        if (response.ok) { const data = await response.json(); setClasses(data); }
    }, [authHeader, backendUrl]);

    useEffect(() => {
        Promise.all([getParents(), getClasses()]).finally(() => setLoading(false));
    }, []);

    const childLookup = useMemo(() => {
        const map = {};
        classes.forEach(sc => {
            sc.departments.forEach(dept => {
                dept.children.forEach(child => {
                    map[child.id] = { schoolclass_id: String(sc.id), department_id: String(dept.id) };
                });
            });
        });
        return map;
    }, [classes]);

    const deptOptions = useMemo(() => {
        if (!selectedClassId) return [];
        const sc = classes.find(c => String(c.id) === selectedClassId);
        return sc ? sc.departments : [];
    }, [classes, selectedClassId]);

    const filteredParents = useMemo(() => {
        return parents.filter(p => {
            if (p.is_deleted && !showDeleted) return false;
            if (!p.is_registered && !p.is_deleted && !showInactive) return false;
            if (!showNoChildren && (!p.children || p.children.length === 0)) return false;
            if (!selectedClassId && !selectedDeptId) return true;
            if (!p.children || p.children.length === 0) return false;
            return p.children.some(child => {
                const info = childLookup[child.child_id];
                if (!info) return false;
                if (selectedClassId && info.schoolclass_id !== selectedClassId) return false;
                if (selectedDeptId && info.department_id !== selectedDeptId) return false;
                return true;
            });
        });
    }, [parents, childLookup, selectedClassId, selectedDeptId, showNoChildren, showInactive, showDeleted]);

    const handleClassChange = (e) => { setSelectedClassId(e.target.value); setSelectedDeptId(''); };

    const handleDelete = useCallback(async (parent) => {
        if (!window.confirm(t('parents.confirm_delete', { name: `${parent.firstname} ${parent.lastname}` }))) return;
        try {
            const response = await fetch(backendUrl + 'api/delete-parent/', {
                method: 'POST',
                headers: authHeader(),
                body: JSON.stringify({ id: parent.profile_id }),
            });
            if (response.status === 401) { logoutUser(); return; }
            if (!response.ok) { showToast(t('common.network_error'), 'error'); return; }
        } catch {
            showToast(t('common.network_error'), 'error');
            return;
        }
        await getParents();
    }, [authHeader, backendUrl, getParents, logoutUser, showToast, t]);

    const handleRestore = useCallback(async (parent) => {
        try {
            const response = await fetch(backendUrl + 'api/restore-parent/', {
                method: 'POST',
                headers: authHeader(),
                body: JSON.stringify({ id: parent.profile_id }),
            });
            if (response.status === 401) { logoutUser(); return; }
            if (!response.ok) { showToast(t('common.network_error'), 'error'); return; }
        } catch {
            showToast(t('common.network_error'), 'error');
            return;
        }
        showToast(t('parents.restored', { name: `${parent.firstname} ${parent.lastname}` }), 'success');
        await getParents();
    }, [authHeader, backendUrl, getParents, logoutUser, showToast, t]);

    const handleSendMessage = async () => {
        if (!msgText.trim() || selectedParentIds.length === 0) return;
        setMsgSending(true);
        try {
            await fetch(backendUrl + 'api/send-message/', {
                method: 'POST',
                headers: authHeader(),
                body: JSON.stringify({ profile_ids: selectedParentIds, title: msgTitle, text: msgText }),
            });
        } finally {
            setMsgSending(false);
            setMsgOpen(false);
            setMsgTitle('');
            setMsgText('');
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container sx={{ mt: 10, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PeopleIcon sx={{ color: NAV_BG, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG }}>
                        {t('parents.title')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {selectedParentIds.length > 0 && (
                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={() => setMsgOpen(true)}
                            sx={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, fontFamily: '"Outfit", sans-serif', borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: `0 4px 14px ${AMBER_GLOW}`, '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)` } }}
                        >
                            {t('parents.send_message', { count: selectedParentIds.length })}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/add-parent')}
                        sx={{ background: NAV_BG, fontFamily: '"Outfit", sans-serif', borderRadius: '8px', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#163571' } }}
                    >
                        {t('parents.add')}
                    </Button>
                </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' }}>{t('parents.filter_by_class')}</InputLabel>
                    <Select value={selectedClassId} label={t('parents.filter_by_class')} onChange={handleClassChange}
                        sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', borderRadius: '8px', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: AMBER } }}>
                        <MenuItem value="" sx={{ fontFamily: '"Outfit", sans-serif' }}>{t('parents.all_classes')}</MenuItem>
                        {classes.map(sc => (
                            <MenuItem key={sc.id} value={String(sc.id)} sx={{ fontFamily: '"Outfit", sans-serif' }}>{sc.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {selectedClassId && deptOptions.length > 0 && (
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' }}>{t('parents.filter_by_dept')}</InputLabel>
                        <Select value={selectedDeptId} label={t('parents.filter_by_dept')} onChange={(e) => setSelectedDeptId(e.target.value)}
                            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', borderRadius: '8px', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: AMBER } }}>
                            <MenuItem value="" sx={{ fontFamily: '"Outfit", sans-serif' }}>{t('parents.all_departments')}</MenuItem>
                            {deptOptions.map(d => (
                                <MenuItem key={d.id} value={String(d.id)} sx={{ fontFamily: '"Outfit", sans-serif' }}>{d.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {(selectedClassId || selectedDeptId) && (
                    <Chip
                        label={t('parents.of_parents', { count: filteredParents.length, total: parents.length })}
                        size="small"
                        onDelete={() => { setSelectedClassId(''); setSelectedDeptId(''); }}
                        sx={{ fontFamily: '"Outfit", sans-serif', background: 'rgba(15,45,94,0.08)', color: NAV_BG }}
                    />
                )}

                <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto', alignItems: 'center' }}>
                    <FormControlLabel
                        control={<Switch size="small" checked={showNoChildren} onChange={(e) => setShowNoChildren(e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#6366f1' } }} />}
                        label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#4338ca' }}>{t('parents.show_no_children')}</Typography>}
                    />
                    <FormControlLabel
                        control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AMBER }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: AMBER } }} />}
                        label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#92400e' }}>{t('parents.show_inactive')}</Typography>}
                    />
                    <FormControlLabel
                        control={<Switch size="small" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#b91c1c' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#b91c1c' } }} />}
                        label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#b91c1c' }}>{t('parents.show_deleted')}</Typography>}
                    />
                </Box>
            </Box>

            <ParentsComponent
                parents={filteredParents}
                onSelectionChange={setSelectedParentIds}
                onEdit={(p) => navigate(`/add-parent/${p.profile_id}`)}
                onDelete={handleDelete}
                onRestore={handleRestore}
            />

            {/* Send Message Dialog */}
            <Dialog open={msgOpen} onClose={() => setMsgOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG }}>
                    {selectedParentIds.length === 1
                        ? t('parents.send_to', { count: selectedParentIds.length })
                        : t('parents.send_to_plural', { count: selectedParentIds.length })}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField label={t('parents.subject')} fullWidth size="small" value={msgTitle} onChange={(e) => setMsgTitle(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER }, '& .MuiInputLabel-root.Mui-focused': { color: AMBER } }}
                        inputProps={{ style: { fontFamily: '"Outfit", sans-serif' } }} />
                    <RichTextEditor value={msgText} onChange={setMsgText} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setMsgOpen(false)} sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', color: '#64748b' }}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={handleSendMessage} disabled={!msgText.trim() || msgSending}
                        startIcon={msgSending ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                        sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderRadius: '8px', background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)` } }}>
                        {msgSending ? t('parents.sending') : t('parents.send')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ParentsPage;
